// 📁 src/components/BacktestCard.tsx
import React, { useState, useRef, useEffect } from 'react';
import {
  Clock, AlertTriangle, Edit, Trash2, Eye,
  CheckCircle2, Activity, TrendingUp, TrendingDown,
  Calendar, Timer, BarChart3, Zap, Share2, X,
  Mail, Send, MessageCircle, Twitter
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import dayjs from 'dayjs';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/utils/cn';

// 🔥 Apsauga nuo balto ekrano lūžimo
class CardErrorBoundary extends React.Component<any, { hasError: boolean, error: any }> {
  constructor(props: any) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error: any) { return { hasError: true, error }; }
  render() { 
    if (this.state.hasError) return <div className="p-4 bg-red-50 text-red-500 rounded-xl text-xs">Kortelės UI Klaida: {this.state.error?.message}</div>; 
    return this.props.children; 
  }
}

function pickRowFromProps(props: any): any {
  if (props && props.id && (props.title || props.bot_name || props.bot_title)) return props;
  const row = props?.row ?? props?.data ?? props?.item ?? props;
  if (row && typeof row === 'object') { if (row.kpi) delete row.kpi; if (row.hasKPI) delete row.hasKPI; }
  return row;
}

const NULLABLE_VALUES = ['nullable', 'null', 'undefined', 'nan', '', 'custom'];
function sanitizeField(val: any): string {
  if (val === null || val === undefined) return '';
  const s = String(val).trim();
  if (NULLABLE_VALUES.includes(s.toLowerCase())) return '';
  return s;
}

const getExchangeDisplayName = (exchangeName: string): string => {
  const clean = sanitizeField(exchangeName);
  if (!clean) return '\u2014';
  return clean.charAt(0).toUpperCase() + clean.slice(1).toLowerCase();
};

const STRATEGY_MAP: Record<string, string> = {
  'trend': 'Trend Following', 'scalping': 'Scalping', 'support-resistance': 'Support & Resistance',
  'candle-patterns': 'Candle Patterns', 'swing-trading': 'Swing Trading', 'chart-patterns': 'Chart Patterns', 'fvg': 'Fair Value Gap (FVG)'
};

const statusConfig: Record<string, { icon: React.ReactNode; label: string; color: string; bg: string }> = {
  completed: { icon: <CheckCircle2 className="w-3.5 h-3.5" />, label: 'Completed', color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-900/30 border-emerald-200 dark:border-emerald-800/40' },
  running: { icon: <Activity className="w-3.5 h-3.5 animate-pulse" />, label: 'Running', color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800/40' },
  queued: { icon: <Clock className="w-3.5 h-3.5" />, label: 'Queued', color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-900/30 border-amber-200 dark:border-amber-800/40' },
  failed: { icon: <AlertTriangle className="w-3.5 h-3.5" />, label: 'Failed', color: 'text-red-600 dark:text-red-400', bg: 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800/40' },
  error: { icon: <AlertTriangle className="w-3.5 h-3.5" />, label: 'Error', color: 'text-red-600 dark:text-red-400', bg: 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800/40' }
};

const defaultStatus = { icon: <Clock className="w-3.5 h-3.5" />, label: 'Unknown', color: 'text-gray-500 dark:text-gray-400', bg: 'bg-gray-50 dark:bg-gray-700/50 border-gray-200 dark:border-gray-600' };

function formatCompactNumber(val: number): string {
  const abs = Math.abs(val || 0);
  if (abs >= 1_000_000) return (val / 1_000_000).toFixed(1) + 'M';
  if (abs >= 1_000) return (val / 1_000).toFixed(1) + 'k';
  return (val || 0).toFixed(2);
}

const daysToLabel = (days: number): string => {
  if (days >= 360) return '1 Year'; if (days >= 175) return '6 Months'; if (days >= 85) return '3 Months';
  if (days >= 58) return '2 Months'; if (days >= 28) return '1 Month'; if (days >= 13) return '2 Weeks';
  if (days >= 6) return '1 Week'; return `${days} Days`;
};

const BacktestCardContent: React.FC<any> = (props) => {
  const { onView, onEdit, onDelete } = props;
  const routerNavigate = useNavigate();
  
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const shareRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => { if (shareRef.current && !shareRef.current.contains(e.target as Node)) setShareOpen(false); };
    if (shareOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [shareOpen]);

  const data = pickRowFromProps(props);
  if (!data) return null;

  const title = String(data.bot_title || data.title || data.bot_name || 'Untitled').replace(/\s*\(Backtest\)\s*$/i, '').trim();

  let rawPairsData: any[] = [];
  try { rawPairsData = typeof data.pairs_data === 'string' ? JSON.parse(data.pairs_data) : (data.pairs_data || data.pairs_stats_top10 || []); } catch(e) {}
  if (!Array.isArray(rawPairsData)) rawPairsData = [];

  const curr = data.quote_currency || data.quoteCurrency || 'USDC';
  let configSnap: any = {};
  try { configSnap = typeof data?.config_snapshot === 'string' ? JSON.parse(data.config_snapshot) : (data?.config_snapshot || {}); } catch(e) {}

  const primaryTimeInterval = sanitizeField(data?.primary_time_interval) || sanitizeField(configSnap?.primary_time_interval) || sanitizeField(configSnap?.time_interval) || '5m';
  const strategyRaw = String(data.strategy_key || data.strategy || 'Unknown');
  let strategyText = STRATEGY_MAP[strategyRaw.toLowerCase()] || strategyRaw.charAt(0).toUpperCase() + strategyRaw.slice(1);
  if (strategyRaw.toLowerCase().includes('trend')) strategyText = 'Trend Following';

  const exchangeText = getExchangeDisplayName(data.exchange ?? '');

  let initialBalance = Number(data?.account_balance || data?.initial_capital || configSnap?.account_balance || 0);
  let sumCost = 0; let sumGrossProfit = 0; let sumFees = 0; let sumVolume = 0;

  rawPairsData.forEach((item: any) => {
    sumCost += Number(item.total_cost || 0);
    sumGrossProfit += Number(item.profit_amount || item.gross_profit || 0); 
    sumFees += Number(item.total_fees || item.fees || 0); 
    sumVolume += Number(item.total_volume || 0);
  });

  if (initialBalance <= 0 && sumCost > 0) initialBalance = sumCost;
  else if (initialBalance <= 0) initialBalance = 4000;

  const finalCost = Number(data.total_cost_usd || data.total_cost || 0) || sumCost;
  const finalFees = Number(data.fees_total_usd || data.total_fees || data.fee || 0) || sumFees;
  const finalVolume = Number(data.total_volume_usd || data.total_volume || 0) || sumVolume;
  
  // 🔥 TIKSLUS NET PROFIT SKAIČIAVIMAS (-10.87 - 22.36 = -33.23)
  const finalNetProfit = sumGrossProfit - finalFees; 
  const finalProfitPct = initialBalance > 0 ? (finalNetProfit / initialBalance) * 100 : 0;

  let turnoverVal = data.turnover && data.turnover !== '0' && data.turnover !== '0.00' ? data.turnover : (initialBalance > 0 && finalVolume > 0 ? (finalVolume / initialBalance).toFixed(2) : '-');
  if (turnoverVal === '0.00') turnoverVal = '-';

  const tradesCount = data.total_trades || data.trades_count || 0;
  const winRateDisplay = Number(data.win_rate_pct || 0).toFixed(2);
  
  // 🔥 TIKSLUS DRAWDOWN SKAIČIAVIMAS IŠ ISTORIJOS (KAD NERODYTŲ 0.00%)
  let maxDrawdownPct = Number(data.max_drawdown_pct || data.max_drawdown || 0);
  if (maxDrawdownPct === 0 && data.trade_history) {
    try {
      const trades = typeof data.trade_history === 'string' ? JSON.parse(data.trade_history) : data.trade_history;
      if (Array.isArray(trades) && trades.length > 0) {
        let peak = initialBalance; let currentEq = initialBalance; let maxDd = 0;
        const sortedTrades = [...trades].sort((a, b) => new Date(a.closed_at || a.closedAt || a.trade_time || a.time || 0).getTime() - new Date(b.closed_at || b.closedAt || b.trade_time || b.time || 0).getTime());
        sortedTrades.forEach(t => {
          const fee = Number(t.fee || t.fees || 0);
          const rawProfit = Number(t.profit_amount || t.profitAmount || t.profit || t.pl || 0);
          const netTradeProfit = t.net_profit !== undefined ? Number(t.net_profit) : (rawProfit - fee);
          currentEq += netTradeProfit;
          if (currentEq > peak) peak = currentEq;
          const drop = ((peak - currentEq) / peak) * 100;
          if (drop > maxDd) maxDd = drop;
        });
        maxDrawdownPct = maxDd;
      }
    } catch(e) {}
  }
  if (maxDrawdownPct === 0 && finalProfitPct < 0) maxDrawdownPct = Math.abs(finalProfitPct);

  const isRunning = data.status === 'running' || data.status === 'queued';
  const isProfitable = finalNetProfit >= 0;
  const createdAt = data.created_at ? dayjs(data.created_at) : null;

  // 🔥 PERIODO PAUTOMATINIS VERTIMAS Į TEKSTĄ ("1 Month")
  const rawPeriodLabel = String(data.period || data.period_label || configSnap?.period || '');
  let diffDays = Number(data.period_days || data.days || configSnap?.period_days || 0);

  if (diffDays <= 0 && rawPeriodLabel.includes('to')) {
      const parts = rawPeriodLabel.split('to');
      if (parts.length === 2) {
          const start = new Date(parts[0].trim()).getTime();
          const end = new Date(parts[1].trim()).getTime();
          if (!isNaN(start) && !isNaN(end)) diffDays = Math.round((end - start) / 86400000);
      }
  }

  let periodDisplay = '\u2014';
  if (diffDays > 0) periodDisplay = daysToLabel(diffDays);
  else if (rawPeriodLabel && rawPeriodLabel.length > 3 && rawPeriodLabel !== 'null' && rawPeriodLabel !== 'undefined') {
      periodDisplay = rawPeriodLabel.includes('to') ? '1 Month' : rawPeriodLabel;
  } else periodDisplay = '1 Month';

  const status = statusConfig[data.status] || defaultStatus;

  // 🔥 PILNAS SOCIALINIŲ TINKLŲ SĄRAŠAS
  const buildShareText = (): string => {
    let text = `Backtest: ${title}\nStrategy: ${strategyText} | Exchange: ${exchangeText}\nPrimary: ${primaryTimeInterval} | Period: ${periodDisplay}\nNet Profit: ${finalProfitPct > 0 ? '+' : ''}${finalProfitPct.toFixed(2)}% (${finalNetProfit > 0 ? '+' : ''}${finalNetProfit.toFixed(2)} ${curr})\nTrades: ${tradesCount} | Win Rate: ${winRateDisplay}%\nMax Drawdown: ${maxDrawdownPct.toFixed(2)}%\n`;
    if (rawPairsData.length > 0) text += `Pairs: ${rawPairsData.map((p: any) => String(p.symbol || p.pair || '').replace(new RegExp(`${curr}$`, 'i'), '')).filter(Boolean).join(', ')}\n`;
    return text;
  };

  const shareLinks = [
    { name: 'Telegram', icon: <Send className="w-4 h-4" />, color: 'hover:bg-sky-50 text-gray-600 hover:text-sky-600', url: () => `https://t.me/share/url?text=${encodeURIComponent(buildShareText())}` },
    { name: 'Twitter / X', icon: <Twitter className="w-4 h-4" />, color: 'hover:bg-gray-100 text-gray-600 hover:text-gray-900', url: () => `https://twitter.com/intent/tweet?text=${encodeURIComponent(buildShareText())}` },
    { name: 'Facebook', icon: <span className="w-4 h-4 flex items-center justify-center font-bold text-sm">f</span>, color: 'hover:bg-blue-50 text-gray-600 hover:text-blue-700', url: () => `https://www.facebook.com/sharer/sharer.php?quote=${encodeURIComponent(buildShareText())}` },
    { name: 'WhatsApp', icon: <MessageCircle className="w-4 h-4" />, color: 'hover:bg-green-50 text-gray-600 hover:text-green-600', url: () => `https://wa.me/?text=${encodeURIComponent(buildShareText())}` },
    { name: 'Viber', icon: <MessageCircle className="w-4 h-4" />, color: 'hover:bg-violet-50 text-gray-600 hover:text-violet-600', url: () => `viber://forward?text=${encodeURIComponent(buildShareText())}` },
    { name: 'LinkedIn', icon: <span className="w-4 h-4 flex items-center justify-center font-bold text-[11px]">in</span>, color: 'hover:bg-blue-50 text-gray-600 hover:text-blue-700', url: () => `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}&summary=${encodeURIComponent(buildShareText())}` },
    { name: 'Reddit', icon: <span className="w-4 h-4 flex items-center justify-center font-bold text-sm leading-none">R</span>, color: 'hover:bg-orange-50 text-gray-600 hover:text-orange-500', url: () => `https://reddit.com/submit?title=${encodeURIComponent(title)}&selftext=true&text=${encodeURIComponent(buildShareText())}` },
    { name: 'TikTok', icon: <span className="w-4 h-4 flex items-center justify-center font-bold text-[11px] leading-none">Tk</span>, color: 'hover:bg-gray-100 text-gray-600 hover:text-gray-900', url: () => 'copy' },
    { name: 'YouTube', icon: <span className="w-4 h-4 flex items-center justify-center font-bold text-[11px] leading-none">YT</span>, color: 'hover:bg-red-50 text-gray-600 hover:text-red-600', url: () => 'copy' },
    { name: 'Discord', icon: <span className="w-4 h-4 flex items-center justify-center font-bold text-[11px] leading-none">Dc</span>, color: 'hover:bg-indigo-50 text-gray-600 hover:text-indigo-600', url: () => 'copy' },
    { name: 'Pinterest', icon: <span className="w-4 h-4 flex items-center justify-center font-bold text-sm leading-none">P</span>, color: 'hover:bg-red-50 text-gray-600 hover:text-red-700', url: () => `https://pinterest.com/pin/create/button/?description=${encodeURIComponent(buildShareText())}` },
    { name: 'Tumblr', icon: <span className="w-4 h-4 flex items-center justify-center font-bold text-sm leading-none">t</span>, color: 'hover:bg-blue-50 text-gray-600 hover:text-blue-800', url: () => `https://www.tumblr.com/widgets/share/tool?posttype=text&content=${encodeURIComponent(buildShareText())}` },
    { name: 'Email', icon: <Mail className="w-4 h-4" />, color: 'hover:bg-gray-100 text-gray-600 hover:text-gray-700', url: () => `mailto:?subject=${encodeURIComponent(`Backtest: ${title}`)}&body=${encodeURIComponent(buildShareText())}` },
    { name: 'Copy Text', icon: <span className="w-4 h-4 flex items-center justify-center font-bold text-xs">Cc</span>, color: 'hover:bg-gray-100 text-gray-600 hover:text-gray-700', url: () => 'copy' }
  ];

  const handleShare = (urlFn: () => string) => {
    const url = urlFn();
    if (url === 'copy') {
      navigator.clipboard.writeText(buildShareText());
      toast.success('Copied to clipboard');
    } else {
      window.open(url, '_blank', 'noopener,noreferrer,width=600,height=500');
    }
    setShareOpen(false);
  };

  // 🔥 TIESIOGINIS EDIT PATAISYMAS 
  const handleEditFromBacktest = (e?: React.MouseEvent) => {
    e?.preventDefault(); e?.stopPropagation();
    const targetBotId = data.bot_id || data.id;
    if (!targetBotId) return toast.error('Bot ID not found');
    
    if (onEdit) {
      onEdit(targetBotId);
    } else {
      routerNavigate(`/bots/${targetBotId}/edit`);
    }
  };

  const handleDeleteClick = async (e?: React.MouseEvent) => {
    e?.preventDefault(); e?.stopPropagation();
    if (loadingDelete) return;
    if (!window.confirm('Are you sure you want to delete this backtest?')) return;
    setLoadingDelete(true);
    onDelete(data.id);
  };

  return (
    <div className="group bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700/80 shadow-sm hover:shadow-lg transition-all duration-300 flex flex-col h-full relative overflow-hidden">
      {isRunning && (
        <div className="absolute top-0 left-0 right-0 h-1 bg-gray-100 dark:bg-gray-700 z-10">
          <div className="h-full bg-gradient-to-r from-blue-500 to-cyan-400 transition-all duration-1000 ease-linear rounded-r" style={{ width: `${Math.min(100, Math.max(5, data.progress || 0))}%` }} />
        </div>
      )}

      <div className="p-5 pb-0 flex flex-col flex-grow">
        <div className="mb-3">
          <h3 className="font-semibold text-base text-gray-900 dark:text-white truncate leading-tight" title={title}>{title}</h3>
          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
            <span className={cn('inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border', status.color, status.bg)}>
              {status.icon}{status.label}
            </span>
            {createdAt && (
              <span className="text-[11px] text-gray-400 dark:text-gray-500 flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {createdAt.format('MMM D, YYYY HH:mm')}
              </span>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm mb-4">
          <div className="flex items-center gap-1.5"><Zap className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" /><span className="text-gray-500 dark:text-gray-400 text-xs">Strategy</span></div>
          <span className="text-right text-xs font-medium text-gray-800 dark:text-gray-200 truncate">{strategyText}</span>

          <div className="flex items-center gap-1.5"><BarChart3 className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" /><span className="text-gray-500 dark:text-gray-400 text-xs">Exchange</span></div>
          <span className="text-right text-xs font-medium text-gray-800 dark:text-gray-200">{exchangeText}</span>

          <div className="flex items-center gap-1.5"><Timer className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" /><span className="text-gray-500 dark:text-gray-400 text-xs">Primary</span></div>
          <span className="text-right text-xs font-semibold text-blue-600 dark:text-blue-400">{primaryTimeInterval}</span>

          <div className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" /><span className="text-gray-500 dark:text-gray-400 text-xs">Period</span></div>
          <span className="text-right text-xs font-medium text-gray-800 dark:text-gray-200">{periodDisplay}</span>
        </div>

        <div className="grid grid-cols-2 gap-2 mb-4">
          <div className={cn('rounded-xl px-3 py-2.5 border', isProfitable ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-100 dark:border-emerald-800/30' : 'bg-red-50 dark:bg-red-900/20 border-red-100 dark:border-red-800/30')}>
            <div className="flex items-center gap-1 mb-0.5">
              {isProfitable ? <TrendingUp className="w-3 h-3 text-emerald-500" /> : <TrendingDown className="w-3 h-3 text-red-500" />}
              <span className="text-[10px] uppercase tracking-wider font-medium text-gray-500 dark:text-gray-400">Net Profit %</span>
            </div>
            <span className={cn('text-lg font-bold leading-tight', isProfitable ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400')}>
              {finalProfitPct > 0 ? '+' : ''}{(finalProfitPct || 0).toFixed(2)}%
            </span>
          </div>

          <div className="rounded-xl px-3 py-2.5 bg-gray-50 dark:bg-gray-700/50 border border-gray-100 dark:border-gray-600/50">
            <div className="flex items-center gap-1 mb-0.5"><BarChart3 className="w-3 h-3 text-gray-400" /><span className="text-[10px] uppercase tracking-wider font-medium text-gray-500 dark:text-gray-400">Trades</span></div>
            <span className="text-lg font-bold text-gray-900 dark:text-white leading-tight">{tradesCount}</span>
          </div>

          <div className="rounded-xl px-3 py-2.5 bg-gray-50 dark:bg-gray-700/50 border border-gray-100 dark:border-gray-600/50">
            <div className="flex items-center gap-1 mb-0.5"><Activity className="w-3 h-3 text-gray-400" /><span className="text-[10px] uppercase tracking-wider font-medium text-gray-500 dark:text-gray-400">Win Rate</span></div>
            <span className="text-lg font-bold text-gray-900 dark:text-white leading-tight">{winRateDisplay}%</span>
          </div>

          <div className={cn('rounded-xl px-3 py-2.5 border', isProfitable ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-100 dark:border-emerald-800/30' : 'bg-red-50 dark:bg-red-900/20 border-red-100 dark:border-red-800/30')}>
            <div className="flex items-center gap-1 mb-0.5">
              {isProfitable ? <TrendingUp className="w-3 h-3 text-emerald-500" /> : <TrendingDown className="w-3 h-3 text-red-500" />}
              <span className="text-[10px] uppercase tracking-wider font-medium text-gray-500 dark:text-gray-400">Net Profit</span>
            </div>
            <span className={cn('text-base font-bold leading-tight', isProfitable ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400')}>
              {finalNetProfit > 0 ? '+' : ''}{formatCompactNumber(finalNetProfit)}<span className="text-[10px] font-medium ml-0.5 opacity-70">{curr}</span>
            </span>
          </div>
        </div>

        {rawPairsData.length > 0 && (
          <div className="mb-4">
            <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">Pairs Performance (Gross)</div>
            <div className="space-y-1.5 max-h-44 overflow-y-auto custom-scrollbar">
              {rawPairsData.map((pair: any, idx: number) => {
                const pAmt = Number(pair.profit_amount || pair.gross_profit || 0); 
                const pPct = initialBalance > 0 ? (pAmt / initialBalance) * 100 : 0;
                
                const isPositive = pAmt >= 0;
                let sym = String(pair.symbol || pair.pair || '');
                if (sym && !sym.includes('/')) sym = sym.replace(new RegExp(`${curr}$`, 'i'), '') + '/' + curr;
                
                return (
                  <div key={idx} className="flex justify-between items-center text-xs py-2 px-3 rounded-lg bg-blue-50/80 dark:bg-blue-900/15 border border-blue-100/60 dark:border-blue-800/20">
                    <span className="font-medium text-gray-700 dark:text-gray-300">{sym}</span>
                    <div className="flex items-center gap-2">
                      <span className={cn('font-semibold tabular-nums', isPositive ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400')}>{isPositive ? '+' : ''}{(pPct||0).toFixed(2)}%</span>
                      <span className={cn('font-medium tabular-nums', isPositive ? 'text-emerald-500/70 dark:text-emerald-400/70' : 'text-red-500/70 dark:text-red-400/70')}>{isPositive ? '+' : ''}{(pAmt||0).toFixed(2)} {curr}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="grid grid-cols-4 gap-1.5 py-3 border-t border-gray-100 dark:border-gray-700/60 text-center">
          <div><div className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-0.5">Turnover</div><div className="text-xs font-semibold text-gray-700 dark:text-gray-300">{turnoverVal !== '-' ? `${turnoverVal}\u00d7` : '\u2014'}</div></div>
          <div><div className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-0.5">Cost</div><div className="text-xs font-semibold text-gray-700 dark:text-gray-300">{formatCompactNumber(finalCost || 0)}</div></div>
          <div><div className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-0.5">Fees</div><div className="text-xs font-semibold text-red-500 dark:text-red-400">{formatCompactNumber(finalFees || 0)}</div></div>
          <div><div className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-0.5">Drawdown</div><div className="text-xs font-semibold text-amber-600 dark:text-amber-400">{(maxDrawdownPct || 0).toFixed(2)}%</div></div>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-0 border-t border-gray-100 dark:border-gray-700/60 mt-auto">
        <button className="flex items-center justify-center gap-1.5 py-3 text-xs font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors border-r border-gray-100 dark:border-gray-700/60" onClick={(e) => { e.preventDefault(); e.stopPropagation(); data.id && onView(data.id); }}><Eye size={13} /><span>Details</span></button>
        
        {/* 🔥 PATAISYTAS EDIT MYGTUKAS */}
        <button className="flex items-center justify-center gap-1.5 py-3 text-xs font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors border-r border-gray-100 dark:border-gray-700/60" onClick={(e) => { handleEditFromBacktest(e); }}><Edit size={13} /><span>Edit</span></button>
        
        {/* 🔥 GRĄŽINTAS SHARE MYGTUKAS */}
        <div className="relative border-r border-gray-100 dark:border-gray-700/60" ref={shareRef}>
          <button className="flex items-center justify-center gap-1.5 py-3 w-full text-xs font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShareOpen(!shareOpen); }}><Share2 size={13} /><span>Share</span></button>
          {shareOpen && (
            <div className="absolute bottom-full right-0 mb-2 w-52 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 z-50 overflow-hidden">
              <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100 dark:border-gray-700/60"><span className="text-xs font-semibold text-gray-700 dark:text-gray-300">Share via</span><button onClick={(e) => { e.stopPropagation(); setShareOpen(false); }} className="p-0.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700"><X className="w-3.5 h-3.5 text-gray-400" /></button></div>
              <div className="py-1 max-h-48 overflow-y-auto custom-scrollbar">
                {shareLinks.map((link) => (
                  <button key={link.name} onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleShare(link.url); }} className={cn('flex items-center gap-2.5 w-full px-3 py-2 text-xs text-gray-600 dark:text-gray-400 transition-colors hover:bg-gray-50', link.color)}>
                    {link.icon}<span className="font-medium">{link.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        
        <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleDeleteClick(e); }} disabled={loadingDelete} className="flex items-center justify-center gap-1.5 py-3 text-xs font-medium text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"><Trash2 size={13} /><span>Delete</span></button>
      </div>
    </div>
  );
};

export default function SafeBacktestCard(props: any) {
  return (
    <CardErrorBoundary>
      <BacktestCardContent {...props} />
    </CardErrorBoundary>
  );
}